import { useCallback, useLayoutEffect, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { TableCell, TableHead, TableRow } from '@savvy-kit/components/table';
import { cn } from '@savvy-kit/lib/utils';
import { GripVertical } from 'lucide-react';

import type { DataTableSlots } from '../types/data-table-slots';
import { useDataTableContext } from './data-table-context';

export function DefaultHeaderGroup<TData>({
  headerGroup: _headerGroup,
  index: _index,
  ...props
}: React.ComponentProps<NonNullable<DataTableSlots<TData>['headerGroup']>>) {
  return <TableRow {...props} />;
}

export function DefaultHeaderCell<TData>({
  header,
  headerGroupIndex: _headerGroupIndex,
  headerIndex: _headerIndex,
  children,
  style,
  className,
  ...props
}: React.ComponentProps<NonNullable<DataTableSlots<TData>['headerCell']>>) {
  const { columnResizingEnabled, columnOrderingEnabled, setMinColumnSize, resizeColumn } = useDataTableContext();
  const contentRef = useRef<HTMLDivElement>(null);
  const columnOrderable = columnOrderingEnabled && header.column.columnDef.meta?.columnOrdering?.enabled !== false;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: header.column.id,
    disabled: !columnOrderable,
  });

  // Measured once on mount (and when column id changes). If header content changes after mount
  // (e.g. sort indicators appearing), the stored min may be slightly stale. Adding `children`
  // to deps would re-measure on every render because React elements are referentially unstable.
  // A ResizeObserver on contentRef would be the correct fix if this becomes a problem.
  useLayoutEffect(() => {
    if (!columnResizingEnabled || !contentRef.current) return;
    const wrapper = contentRef.current;
    const th = wrapper.closest('th');
    if (!th) return;
    wrapper.style.width = 'max-content';
    const naturalWidth = wrapper.offsetWidth;
    wrapper.style.width = '';
    const paddingLeft = parseFloat(getComputedStyle(th).paddingLeft) || 0;
    const paddingRight = parseFloat(getComputedStyle(th).paddingRight) || 0;
    setMinColumnSize(header.column.id, naturalWidth + paddingLeft + paddingRight);
  }, [columnResizingEnabled, header.column.id, setMinColumnSize]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        resizeColumn(header.column.id, header.getSize() + 10);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        resizeColumn(header.column.id, Math.max(1, header.getSize() - 10));
      }
    },
    [header, resizeColumn],
  );

  const transformStr = transform ? `translate3d(${transform.x}px, 0, 0)` : undefined;
  const resolvedStyle: React.CSSProperties = {
    ...style,
    ...(columnResizingEnabled && { width: `${header.getSize()}px` }),
    ...(transformStr && { transform: transformStr }),
    ...(transition && { transition }),
    ...(isDragging && { opacity: 0.8, zIndex: 10, position: 'relative' }),
  };

  return (
    <TableHead
      ref={setNodeRef}
      className={cn(
        columnResizingEnabled && 'relative group/th overflow-visible',
        columnOrderable && 'group/orderable',
        className,
        header.column.id === 'select' && '!pl-3 !py-2',
      )}
      style={resolvedStyle}
      {...props}
    >
      {columnOrderable && (
        <button
          type="button"
          className="absolute left-0 top-1/2 -translate-y-1/2 cursor-grab rounded p-0.5 opacity-0 transition-opacity group-hover/orderable:opacity-100 active:cursor-grabbing focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring text-[var(--text-secondary)]"
          aria-label="Drag to reorder column"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-3" />
        </button>
      )}
      <div ref={columnResizingEnabled ? contentRef : undefined} className={cn(columnOrderable && 'pl-4')}>
        {children}
      </div>
      {columnResizingEnabled && header.column.getCanResize() && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={header.getSize()}
          aria-label="Resize column"
          tabIndex={0}
          className={cn(
            'absolute right-0 top-0 h-full w-3 cursor-col-resize select-none touch-none z-10',
            'flex justify-end opacity-0 group-hover/th:opacity-100 focus-visible:opacity-100 focus-visible:outline-none',
            header.column.getIsResizing() && 'opacity-100',
          )}
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          onKeyDown={handleKeyDown}
        >
          <div className={cn('h-full w-1', header.column.getIsResizing() ? 'bg-primary' : 'bg-border')} />
        </div>
      )}
    </TableHead>
  );
}

export function DefaultRow<TData>({
  row: _row,
  index: _index,
  ...props
}: React.ComponentProps<NonNullable<DataTableSlots<TData>['row']>>) {
  return <TableRow {...props} />;
}

export function DefaultCell<TData>({
  cell,
  rowIndex: _rowIndex,
  cellIndex: _cellIndex,
  style,
  className,
  children,
  ...props
}: React.ComponentProps<NonNullable<DataTableSlots<TData>['cell']>>) {
  const { columnResizingEnabled } = useDataTableContext();

  const resolvedStyle = columnResizingEnabled ? { ...style, width: `${cell.column.getSize()}px` } : style;
  const fillCell = cell.column.columnDef.meta?.cell?.fill === true;
  // The kit's `select` column carries its own padding; `actions` and
  // `householdsCount` are legacy bypasses for cells whose content (icons,
  // buttons) shouldn't be wrapped in the truncate div but still wants the
  // table-wide cell padding. Prefer `meta.cell.fill: true` in new code.
  const skipTruncateWrapper = fillCell || ['select', 'actions', 'householdsCount'].includes(cell.column.id);

  return (
    <TableCell
      className={cn(
        columnResizingEnabled && 'overflow-hidden',
        className,
        cell.column.id === 'select' && '!pl-3 !py-2',
        fillCell && '!p-0',
      )}
      style={resolvedStyle}
      {...props}
    >
      {skipTruncateWrapper ? (
        children
      ) : (
        <div className={columnResizingEnabled ? 'truncate' : undefined}>{children}</div>
      )}
    </TableCell>
  );
}

export function DefaultFooterGroup<TData>({
  footerGroup: _footerGroup,
  index: _index,
  ...props
}: React.ComponentProps<NonNullable<DataTableSlots<TData>['footerGroup']>>) {
  return <TableRow {...props} />;
}

export function DefaultFooterCell<TData>({
  footer: _footer,
  footerGroupIndex: _footerGroupIndex,
  footerIndex: _footerIndex,
  ...props
}: React.ComponentProps<NonNullable<DataTableSlots<TData>['footerCell']>>) {
  return <TableCell {...props} />;
}
